/**
 * On-device wake-word detection (openWakeWord models, run with ONNX Runtime).
 * Audio is 16 kHz mono in 80 ms chunks (1280 samples) on the int16 scale. Nothing leaves the device.
 *
 * Pipeline per chunk: raw audio → mel spectrogram (8 frames) → speech embedding of the last 76 frames
 * → wake-word model over the last 16 embeddings → score 0..1.
 */

export const WAKE_CHUNK = 1280;
const MEL_CONTEXT = 480;
const MEL_WINDOW = 76;
const MEL_BINS = 32;
const EMBED_WINDOW = 16;

interface OrtTensor {
  data: Float32Array | ArrayLike<number>;
  dims: readonly number[];
}
interface OrtSession {
  inputNames: readonly string[];
  outputNames: readonly string[];
  run(feeds: Record<string, OrtTensor>): Promise<Record<string, OrtTensor>>;
}
export interface OrtLike {
  InferenceSession: { create(model: string | Uint8Array, options?: Record<string, unknown>): Promise<OrtSession> };
  Tensor: new (type: 'float32', data: Float32Array, dims: number[]) => OrtTensor;
}

export interface WakeModels {
  melspectrogram: string | Uint8Array;
  embedding: string | Uint8Array;
  wakeword: string | Uint8Array;
}

export class WakeWordDetector {
  private context = new Float32Array(MEL_CONTEXT);
  private mel: Float32Array[] = [];
  private embeddings: Float32Array[] = [];
  private readonly ort: OrtLike;
  private readonly melSession: OrtSession;
  private readonly embedSession: OrtSession;
  private readonly wakeSession: OrtSession;

  private constructor(ort: OrtLike, melSession: OrtSession, embedSession: OrtSession, wakeSession: OrtSession) {
    this.ort = ort;
    this.melSession = melSession;
    this.embedSession = embedSession;
    this.wakeSession = wakeSession;
    this.reset();
  }

  static async create(ort: OrtLike, models: WakeModels, executionProviders: string[] = ['wasm']): Promise<WakeWordDetector> {
    const opts = { executionProviders };
    const [mel, embed, wake] = await Promise.all([
      ort.InferenceSession.create(models.melspectrogram, opts),
      ort.InferenceSession.create(models.embedding, opts),
      ort.InferenceSession.create(models.wakeword, opts),
    ]);
    return new WakeWordDetector(ort, mel, embed, wake);
  }

  /** Clears history so one utterance can't trigger twice. */
  reset(): void {
    this.context = new Float32Array(MEL_CONTEXT);
    this.mel = Array.from({ length: MEL_WINDOW }, () => new Float32Array(MEL_BINS).fill(1));
    this.embeddings = [];
  }

  /** Feeds one 1280-sample chunk; returns the wake-word score (0 until ~1.3 s of audio has been heard). */
  async process(chunk: Float32Array): Promise<number> {
    if (chunk.length !== WAKE_CHUNK) throw new Error(`Wake-word chunks must be ${WAKE_CHUNK} samples.`);
    const input = new Float32Array(MEL_CONTEXT + WAKE_CHUNK);
    input.set(this.context, 0);
    input.set(chunk, MEL_CONTEXT);
    this.context = chunk.slice(WAKE_CHUNK - MEL_CONTEXT);

    const melOut = await this.run(this.melSession, input, [1, input.length]);
    const frames = Math.floor(melOut.data.length / MEL_BINS);
    for (let f = 0; f < frames; f++) {
      const row = new Float32Array(MEL_BINS);
      for (let b = 0; b < MEL_BINS; b++) row[b] = Number(melOut.data[f * MEL_BINS + b]) / 10 + 2;
      this.mel.push(row);
    }
    if (this.mel.length > MEL_WINDOW * 2) this.mel = this.mel.slice(-MEL_WINDOW);

    const window = new Float32Array(MEL_WINDOW * MEL_BINS);
    this.mel.slice(-MEL_WINDOW).forEach((row, i) => window.set(row, i * MEL_BINS));
    const embedOut = await this.run(this.embedSession, window, [1, MEL_WINDOW, MEL_BINS, 1]);
    this.embeddings.push(Float32Array.from(embedOut.data as ArrayLike<number>));
    if (this.embeddings.length > EMBED_WINDOW) this.embeddings.shift();
    if (this.embeddings.length < EMBED_WINDOW) return 0;

    const features = new Float32Array(EMBED_WINDOW * 96);
    this.embeddings.forEach((e, i) => features.set(e, i * 96));
    const wakeOut = await this.run(this.wakeSession, features, [1, EMBED_WINDOW, 96]);
    return Number(wakeOut.data[0]) || 0;
  }

  private async run(session: OrtSession, data: Float32Array, dims: number[]): Promise<OrtTensor> {
    const out = await session.run({ [session.inputNames[0]]: new this.ort.Tensor('float32', data, dims) });
    return out[session.outputNames[0]];
  }
}
